import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-stat-card.css?inline';

/**
 * `<pk-stat-card>` — KPI tile with accent border.
 *
 * Small dashboard tile that pairs a label, a large display value, and an
 * optional muted hint line ("4.2 GB used / 12 backups this month").
 *
 * Slots:
 *   - default — the value (renders inside `.value`, sized large).
 *   - `label` — small uppercase label above the value.
 *   - `sub`   — small muted hint below the value.
 *
 * Attributes:
 *   - `accent` — `primary` | `accent` | `danger` (default `primary`).
 *                Drives the left-edge accent stripe colour.
 */
export class PkStatCard extends HTMLElement {
    static readonly tagName = 'pk-stat-card';

    static get observedAttributes(): string[] {
        return ['accent'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private _accent(): 'primary' | 'accent' | 'danger' {
        const v = this.getAttribute('accent') ?? 'primary';
        return v === 'accent' || v === 'danger' ? v : 'primary';
    }

    private template(): TemplateResult {
        const accent = this._accent();
        return html`
            <style>${styles}</style>
            <div class="stat-card accent-${accent}">
                <div class="label">
                    <slot name="label"></slot>
                </div>
                <div class="value">
                    <slot></slot>
                </div>
                <div class="sub">
                    <slot name="sub"></slot>
                </div>
            </div>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkStatCard.tagName)) {
    customElements.define(PkStatCard.tagName, PkStatCard);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-stat-card': PkStatCard;
    }
}
